import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import useSSE from 'hooks/useSSE';
import { STATUS, SSE_TASKS, NOTIFICATION_TIMEOUT, MAX_NOTIFICATIONS, ANALYTICS_STATUS, SSE_CHANNELS, API_ENDPOINTS } from "constants";
import ControlledDropdown from "components/dropdowns/ControlledDropdown";
import { useFetchData } from "hooks/apiHooks";
import TalkingAssistantAnimation from "components/assistant/TalkingAssistantAnimation";

const NotificationDropdown = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [newNotifications, setNewNotifications] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const {sseSuccess, setSseSuccess, sseError} = useSSE(SSE_CHANNELS.EMAIL_UPDATE, SSE_TASKS.ANALYSE_EMAILS, true);
    const {sseSuccess: emailSentSuccess, setSseSuccess: setEmailSentSuccess, emailSentError} = useSSE(SSE_CHANNELS.EMAIL_UPDATE, SSE_TASKS.SEND_EMAIL, true);
    const { data:activeAvatar, isLoading, error, refetch } = useFetchData(API_ENDPOINTS.AVATARS.ACTIVE);
    const timerRef = useRef(null);

    const memoizedNotifications = useMemo(() => {
        return notifications.length > 0
            ? notifications.map((notification) => ({
                id: notification.id,
                type: notification.type,
                label: notification.type === STATUS.SUCCESS ? notification.event === 'sent' ?
                    `${t('notifications.emailSent')} (${notification.subject})`
                :
                    `${t('notifications.newEmailsAnalyzed')} (${notification.subject})`
                :
                    notification.subject
            }))
            : [{ id: null, label: t('notifications.noNewNotifications') }];
    }, [notifications, t]);
    
    useEffect(() => {
        if (sseSuccess && sseSuccess.status === ANALYTICS_STATUS.ANSWERED) {
            setNotifications((prevNotifications) => [
                { id: sseSuccess.id, subject: sseSuccess.subject, type: STATUS.SUCCESS, event: "analyzed" },
                ...prevNotifications.slice(0, MAX_NOTIFICATIONS - 1)
            ]);

            setNewNotifications(true);
            setDropdownOpen(true);
            setSseSuccess(null);
        }
    }, [sseSuccess, t, setSseSuccess]);

    useEffect(() => {
        if (emailSentSuccess) {
            console.log(emailSentSuccess)
            setNotifications((prevNotifications) => [
                { id: emailSentSuccess.id, subject: emailSentSuccess.subject, type: STATUS.SUCCESS, event: "sent" },
                ...prevNotifications.slice(0, MAX_NOTIFICATIONS - 1)
            ]);

            setNewNotifications(true);
            setDropdownOpen(true);
            setEmailSentSuccess(null);
        }
    }, [emailSentSuccess, t, setEmailSentSuccess]);

    useEffect(() => {
        if (sseError) {
            console.error('SSE connection error:', sseError);
            setNotifications((prevNotifications) => [
                { id: sseError.id, subject: sseError.message, type: STATUS.ERROR },
                ...prevNotifications.slice(0, MAX_NOTIFICATIONS - 1)
            ]);

            setNewNotifications(true);
            setDropdownOpen(true);
        }
    }, [sseError, t]);

    useEffect(() => {
        if (dropdownOpen) {
            timerRef.current = setTimeout(() => {
                setDropdownOpen(false);
            }, NOTIFICATION_TIMEOUT);
        }

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [dropdownOpen]);

    const handleNotificationSelect = useCallback((notification) => {
        console.log(notification)
        setNotifications((prevNotifications) =>
            prevNotifications.filter((n) => n.id !== notification.id)
        );
        setNewNotifications(false);
        setDropdownOpen(false);
        if(notification.type === STATUS.SUCCESS){
            navigate(`/admin/emails/${notification.id}`);
        }
    }, [navigate]);

    return (
        <ControlledDropdown
            items={memoizedNotifications}
            onItemSelect={handleNotificationSelect}
            dropdownOpen={dropdownOpen}
            setDropdownOpen={setDropdownOpen}>
                {dropdownOpen && activeAvatar && !isLoading ?
                    <TalkingAssistantAnimation isReading={false} width={30} height={30} avatar={activeAvatar} style={{borderRadius:"50%", objectFit:"cover"}}/>
                :
                    <>
                        {newNotifications && 
                            <div className="notification d-none d-lg-block d-xl-block" />
                        }
                        <i className={"tim-icons icon-sound-wave"} />
                        <p className="d-lg-none">{t('notifications.iconLabel')}</p>
                    </>
                }
        </ControlledDropdown>
    );
};

export default NotificationDropdown;
